import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { countActiveAdminLogFilters, normalizeAdminLogsFilters } from "@/features/admin/adminFilters";
import { acknowledgeAppLog, loadAdminLogsPage } from "@/features/admin/api/adminClient";
import { AdminLogViewer } from "@/features/admin/components/AdminLogViewer";
import type { AdminLogsFilters } from "@/features/admin/admin.types";

const defaultFilters: AdminLogsFilters = {
  endDate: "",
  page: 1,
  searchText: "",
  severity: "all",
  source: "",
  startDate: "",
};

export function AdminLogsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AdminLogsFilters>(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<Awaited<ReturnType<typeof loadAdminLogsPage>> | null>(null);

  const activeFilterCount = countActiveAdminLogFilters(filters);
  const guardAlertCount = useMemo(() => {
    if (!pageData) {
      return 0;
    }

    return pageData.items.filter((item) =>
      item.quotaResourceKind !== null || item.source === "ai-abuse-review"
    ).length;
  }, [pageData]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void loadAdminLogsPage(normalizeAdminLogsFilters(filters))
      .then((result) => {
        if (!cancelled) {
          setPageData(result);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : t("adminWorkspace.errors.loadLogs"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="space-y-5">
      <section className="rounded-[1.7rem] border border-white/8 bg-white/[0.035] px-5 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-[#b69d7e]">
          {t("adminWorkspace.logs.eyebrow")}
        </p>
        <div className="mt-3 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <h2 className="font-display text-4xl text-[#fff7ef]">{t("adminWorkspace.logs.title")}</h2>
            <p className="mt-3 text-sm leading-7 text-[#d7cab9]/76">
              {t("adminWorkspace.logs.body")}
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#b69d7e]/85">
              {activeFilterCount ? t("adminWorkspace.logs.activeFilters", { count: activeFilterCount }) : t("adminWorkspace.logs.noFilters")}
            </p>
            <p className="mt-2 text-xs text-[#d7cab9]/65">
              {t("adminWorkspace.logs.guardSignals", {
                count: guardAlertCount,
                defaultValue: `${guardAlertCount} AI guardrail alerts on this page.`,
              })}
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <div className="relative xl:col-span-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#9d886d]" />
              <Input
                className="border-white/10 bg-black/20 pl-11 text-[#fff7ef] placeholder:text-[#8f7a63]"
                placeholder={t("adminWorkspace.common.searchMessageOrSource")}
                value={filters.searchText}
                onChange={(event) => setFilters((current) => ({ ...current, page: 1, searchText: event.target.value }))}
              />
            </div>
            <select
              aria-label={t("adminWorkspace.common.filterSeverity")}
              className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none transition focus:border-[#d2b793]/40 focus:ring-2 focus:ring-[#d2b793]/20"
              value={filters.severity}
              onChange={(event) => setFilters((current) => ({
                ...current,
                page: 1,
                severity: event.target.value as AdminLogsFilters["severity"],
              }))}
            >
              <option value="all">{t("adminWorkspace.common.allSeverities")}</option>
              <option value="info">{t("adminWorkspace.logs.severity.info")}</option>
              <option value="warning">{t("adminWorkspace.logs.severity.warning")}</option>
              <option value="error">{t("adminWorkspace.logs.severity.error")}</option>
              <option value="critical">{t("adminWorkspace.logs.severity.critical")}</option>
            </select>
            <Input
              className="border-white/10 bg-black/20 text-[#fff7ef] placeholder:text-[#8f7a63]"
              placeholder={t("adminWorkspace.common.source")}
              value={filters.source}
              onChange={(event) => setFilters((current) => ({ ...current, page: 1, source: event.target.value }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input className="border-white/10 bg-black/20 text-[#fff7ef]" type="date" value={filters.startDate} onChange={(event) => setFilters((current) => ({ ...current, page: 1, startDate: event.target.value }))} />
              <Input className="border-white/10 bg-black/20 text-[#fff7ef]" type="date" value={filters.endDate} onChange={(event) => setFilters((current) => ({ ...current, page: 1, endDate: event.target.value }))} />
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <div className="rounded-[1.4rem] border border-[#7b3430]/35 bg-[#6d2d29]/25 px-5 py-4 text-sm text-[#f6d8d2]">
          {error}
        </div>
      ) : null}

      {loading && !pageData ? (
        <div className="rounded-[1.6rem] border border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
          {t("adminWorkspace.logs.loading")}
        </div>
      ) : (
        <AdminLogViewer
          logs={pageData?.items ?? []}
          onAcknowledge={(logId) => {
            void acknowledgeAppLog(logId)
              .then(() => loadAdminLogsPage(normalizeAdminLogsFilters(filters)))
              .then((nextPage) => setPageData(nextPage))
              .catch((ackError) => setError(ackError instanceof Error ? ackError.message : t("adminWorkspace.errors.acknowledgeLog")));
          }}
        />
      )}

      <div className="flex items-center justify-between gap-3 rounded-[1.5rem] border border-white/8 bg-black/20 px-5 py-4">
        <Button
          disabled={(pageData?.page ?? 1) <= 1 || loading}
          type="button"
          variant="outline"
          onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
        >
          {t("adminWorkspace.common.previous")}
        </Button>
        <span className="text-sm text-[#d7cab9]/70">
          {pageData
            ? t("adminWorkspace.logs.pageSummary", {
                page: pageData.page,
                pageCount: pageData.pageCount,
                totalCount: pageData.totalCount,
              })
            : t("adminWorkspace.common.loadingResults")}
        </span>
        <Button
          disabled={!pageData || pageData.page >= pageData.pageCount || loading}
          type="button"
          variant="outline"
          onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
        >
          {t("adminWorkspace.common.next")}
        </Button>
      </div>
    </div>
  );
}
